import { useEffect, useRef, useState } from "react";
import {
  useForm,
  Controller,
  SubmitHandler,
  FormProvider,
} from "react-hook-form";

import SearchInput from "../SearchInput";
import { AirPlaneIcon, MapPinIcon, SearchIcon, ShipIcon } from "../Icons";
import InputWithLabel from "../InputWithLabel";
import ShippingDropDown from "../ShippingDropDown";

import { getItemFromLocalStore, today } from "../../utils";
import { SimpleBookingFormType } from "./SimpleBooking";

type Props = {
  onSubmit: SubmitHandler<SimpleBookingFormType>;
  isLoading: boolean;
};

export default function QuoteForm({ onSubmit, isLoading }: Props) {
  const [shippingTypePopUp, setShippingTypePopUp] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const methods = useForm<SimpleBookingFormType>();
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = methods;

  // close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShippingTypePopUp(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const persistedData = getItemFromLocalStore("cargo-details");

    if (persistedData) {
      reset(persistedData);
      (document.getElementById("get-quotes-btn") as HTMLButtonElement).click();
    }
  }, []);

  const formValues = watch();

  const onToggle = () => setShippingTypePopUp(!shippingTypePopUp);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex gap-2 items-end">
        {/* From */}
        <Controller
          name="from_json"
          control={control}
          rules={{ required: "Enter a source" }}
          render={({ field }) => (
            <div>
              <label
                className="text-[#344054] text-sm font-medium"
                htmlFor="from"
              >
                From
              </label>
              <SearchInput
                placeholder="Source"
                name="from"
                initialCity={
                  field.value
                    ? `${field.value?.name}, ${field.value?.countryCode}`
                    : ""
                }
                icon={<MapPinIcon className="size-5 text-gray-700" />}
                className="input-new !pl-[40px]"
                onChange={field.onChange}
              />

              <small className="text-red-500 h-[10px] block ml-1">
                {errors.from_json && errors.from_json.message}
              </small>
            </div>
          )}
        />

        {/* To */}
        <Controller
          control={control}
          name="to_json"
          rules={{ required: "Enter a destination" }}
          render={({ field }) => (
            <div>
              <label
                className="text-[#344054] text-sm font-medium"
                htmlFor="to"
              >
                To
              </label>
              <SearchInput
                placeholder="Destination"
                name="to"
                initialCity={
                  field.value
                    ? `${field.value?.name}, ${field.value?.countryCode}`
                    : ""
                }
                icon={<MapPinIcon className="size-5 text-gray-700" />}
                className="input-new !pl-[40px]"
                onChange={field.onChange}
              />

              <small className="text-red-500 h-[10px] block ml-1">
                {errors.to_json && errors.to_json.message}
              </small>
            </div>
          )}
        />

        {/* Date */}
        <div>
          <InputWithLabel
            label="Pickup Date"
            type="date"
            min={today}
            onFocus={(e) => {
              e.currentTarget.showPicker();
            }}
            {...register("pickup_date", { required: "Select a Pickup date" })}
            error={errors.pickup_date?.message}
          />
        </div>

        {/* Shipping Type */}
        <div>
          <label
            htmlFor="freight_type"
            className="text-[#344054] text-sm font-medium block"
          >
            ETA
          </label>

          <div className="relative w-[180px]">
            {/* Trigger */}
            <div
              ref={triggerRef}
              className="input-new flex items-center gap-1 cursor-pointer border p-2 rounded-md"
              onClick={onToggle}
            >
              {formValues.freight_type === "ocean" && (
                <ShipIcon className="size-5 text-gray-700" />
              )}
              {formValues.freight_type === "air" && (
                <AirPlaneIcon className="size-5 text-gray-700" />
              )}
              <p
                className={`${
                  formValues.freight_type ? "text-black" : "text-[#aaa]"
                }`}
              >
                {formValues.freight_type
                  ? `${formValues.freight_type}, ${
                      formValues.freight_type === "ocean"
                        ? formValues?.transportation_by
                        : ""
                    } ${formValues?.weight}`
                  : "Shipping Type"}
              </p>
            </div>

            {/* Dropdown */}
            {shippingTypePopUp && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-2 w-[260px] z-50"
              >
                <ShippingDropDown />
              </div>
            )}
          </div>

          <small className="text-red-500 h-[10px] block ml-1">
            {errors.freight_type || errors.transportation_by || errors.weight
              ? "Select a options"
              : ""}
          </small>
        </div>

        <div>
          {/* Submit Button */}
          <button
            type="submit"
            className="bg-custom-blue-medium h-11 px-2 mb-[10px] rounded-lg text-white disabled:bg-custom-blue-medium/60"
            id="get-quotes-btn"
            disabled={isLoading}
          >
            <SearchIcon className="size-6" />
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
